import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { T } from './declarationTheme';

/* ─────────────────────────────────────────────────────────────
   Stepper vertical minimaliste - sur fond blanc, pas de cards
   ───────────────────────────────────────────────────────────── */
export default function StepperSidebar({ steps, current, completed, onGoto }) {
  const { t } = useTranslation('declaration');
  return (
    <aside
      style={{
        width: 280,
        flexShrink: 0,
        padding: '40px 28px 40px 36px',
        borderRight: `1px solid ${T.line}`,
        background: T.white,
        position: 'sticky',
        top: 56,
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 56px - var(--cb-banniere-h, 0px))',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '1.6px',
          textTransform: 'uppercase',
          color: T.inkMuted,
          marginBottom: 8,
        }}
      >
        {t('stepperSidebar.nouvelleDeclaration')}
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: T.ink, letterSpacing: '-0.01em', marginBottom: 32 }}>
        {t('stepperSidebar.dossierSinistre')}
      </div>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {steps.map((step, i) => {
          const isCurrent = i === current;
          const isDone = completed.includes(i);
          const reachable = isDone || i <= current;

          return (
            <li key={step.key} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => reachable && onGoto(i)}
                disabled={!reachable}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  width: '100%',
                  padding: '12px 0',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: reachable ? 'pointer' : 'default',
                  transition: 'opacity 160ms ease',
                  opacity: reachable ? 1 : 0.5,
                }}
              >
                {/* ─── Cercle numéroté ─── */}
                <span
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1.5px solid ${
                      isCurrent ? T.brand : isDone ? T.brand : T.inkGhost
                    }`,
                    background: isDone ? T.brand : isCurrent ? T.white : T.white,
                    color: isDone ? T.white : isCurrent ? T.brand : T.inkMuted,
                    transition: 'all 180ms ease',
                  }}
                >
                  {isDone ? <CheckOutlined style={{ fontSize: 11 }} /> : i + 1}
                </span>

                {/* ─── Label ─── */}
                <span style={{ minWidth: 0, flex: 1, paddingTop: 2 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 13.5,
                      fontWeight: isCurrent ? 600 : 500,
                      color: isCurrent ? T.ink : isDone ? T.inkSoft : T.inkMuted,
                      lineHeight: 1.4,
                    }}
                  >
                    {step.label}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12,
                      color: T.inkLight,
                      lineHeight: 1.4,
                      marginTop: 2,
                    }}
                  >
                    {step.hint}
                  </span>
                </span>
              </button>

              {/* Ligne de connexion */}
              {i < steps.length - 1 && (
                <span
                  style={{
                    position: 'absolute',
                    left: 13,
                    top: 38,
                    bottom: -8,
                    width: 1.5,
                    background: isDone ? T.brand : T.line,
                    transition: 'background 180ms ease',
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* ─── Pied de sidebar : progression + auto-save ─── */}
      <div style={{ marginTop: 40, paddingTop: 20, borderTop: `1px solid ${T.line}` }}>
        <div style={{ fontSize: 11, color: T.inkMuted, fontWeight: 500 }}>
          {t('stepperSidebar.progression')}
        </div>
        <div
          style={{
            marginTop: 8,
            height: 3,
            borderRadius: 2,
            background: T.lineSoft,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.round((completed.length / steps.length) * 100)}%`,
              background: T.brand,
              transition: 'width 260ms ease',
            }}
          />
        </div>
        <div style={{ fontSize: 11, color: T.inkLight, marginTop: 6 }}>
          {t('stepperSidebar.etapesCompletees', { faites: completed.length, total: steps.length })}
        </div>
      </div>
    </aside>
  );
}
